// @flow
import type { EventSink } from '../event-sink';
import type { UserEvent } from '../user-event';

import {
  appendScriptToHead,
  prependScriptToHead,
  scriptTagWithJS,
} from 'modules/dom';
import type { User } from 'modules/user';
import { identifierAsString, asIdentifier } from 'modules/ecmascript';
import type { Identifier } from 'modules/ecmascript';

export type Specification = $ReadOnly<{|
  /**
   * The Google Tag Manager ID, should start with GTM-
   */
  gtmId: string,
  /**
   * I don't really know what this is for, or how it's different from intialData.
   */
  initialEvents?: { ... },
  /**
   * Data to preload into the data layer, usually for initial contextual info.
   */
  initialData?: { ... },
  dataLayerName: string,
  preview?: string,
  auth?: string,
|}>;

export type Options = $ReadOnly<{|
  /**
   * The Google Tag Manager ID, should start with GTM-
   */
  gtmId: string,
  /**
   * I don't really know what this is for, or how it's different from intialData.
   */
  initialEvents: { ... },
  /**
   * Data to preload into the data layer, usually for initial contextual info.
   */
  initialData: { ... },
  dataLayerName: Identifier,
  preview: string,
  auth: string,
|}>;

export const dataLayerJS = (
  initialData: { ... },
  dataLayerName: Identifier,
): string =>
  `
window.${identifierAsString(dataLayerName)} = window.${identifierAsString(
    dataLayerName,
  )} || [];
window.${identifierAsString(dataLayerName)}.push(${JSON.stringify(initialData)})
`;

export const snippetJS = ({
  gtmId,
  initialEvents,
  dataLayerName,
  preview,
  auth,
}: Options): string =>
  `
      (function(w,d,s,l,i){w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js', ${JSON.stringify(
          initialEvents,
        ).slice(1, -1)}});
        var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl+'&gtm_auth=${auth}&gtm_preview=${preview}&gtm_cookies_win=x';
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','${identifierAsString(
        dataLayerName,
      )}','${gtmId}');`;

export const initializeGtmInstance = (options: Options): Array<mixed> => {
  appendScriptToHead(
    scriptTagWithJS(dataLayerJS(options.initialData, options.dataLayerName)),
  );
  prependScriptToHead(scriptTagWithJS(snippetJS(options)));

  return window[options.dataLayerName];
};

export type UserVariables = $ReadOnly<{|
  user_id: string,
  username: string,
  email: string,
|}>;

export const userVariables: (User | null) => UserVariables | null = (user) =>
  user
    ? {
        user_id: user.id,
        username: user.username,
        email: user.email,
      }
    : null;

class GoogleTagManagerEventSink implements EventSink {
  dataLayerName: Identifier;

  dataLayer: Array<mixed>;

  constructor({
    auth = '',
    preview = '',
    initialData = { ...null },
    initialEvents = { ...null },
    dataLayerName,
    ...rest
  }: Specification) {
    // eslint-disable-next-line fp/no-mutation
    this.dataLayerName = asIdentifier(dataLayerName);
    // eslint-disable-next-line fp/no-mutation
    this.dataLayer = initializeGtmInstance({
      ...rest,
      auth,
      preview,
      initialData,
      initialEvents,
      dataLayerName: this.dataLayerName,
    });
  }

  handleEvent(event: UserEvent, user: User | null) {
    // eslint-disable-next-line fp/no-mutating-methods
    this.dataLayer.push({
      ...userVariables(user),
      ...(event: any),
    });
  }
}

export const fromSpecification: (Specification) => GoogleTagManagerEventSink = (
  specification,
) => new GoogleTagManagerEventSink(specification);

export default GoogleTagManagerEventSink;
